/**
 * Frontend-only RBAC: permission codes and default role-permission matrix.
 * Admin = all true. Supervisor = subset per requirements. Staff = no web access (handled by auth).
 * Overrides can be stored in localStorage (or in-memory fallback in webview) by Permission Management component.
 */

import { getItem, setItem } from '../utils/safe-storage';

export const PERMISSION = {
  // Dashboard
  DASHBOARD_VIEW_JOBS_BY_STATUS: 'DASHBOARD_VIEW_JOBS_BY_STATUS',
  DASHBOARD_ADD_CLIENT: 'DASHBOARD_ADD_CLIENT',
  DASHBOARD_ADD_LEAD: 'DASHBOARD_ADD_LEAD',
  DASHBOARD_ADD_JOB: 'DASHBOARD_ADD_JOB',
  DASHBOARD_ADD_QUOTATION: 'DASHBOARD_ADD_QUOTATION',
  DASHBOARD_ADD_INVOICE: 'DASHBOARD_ADD_INVOICE',
  DASHBOARD_ADD_STAFF: 'DASHBOARD_ADD_STAFF',
  DASHBOARD_SUMMARY_PENDING: 'DASHBOARD_SUMMARY_PENDING',
  DASHBOARD_SUMMARY_JOBS: 'DASHBOARD_SUMMARY_JOBS',
  DASHBOARD_JOBS_TABLE: 'DASHBOARD_JOBS_TABLE',
  DASHBOARD_DUE_INQUIRIES: 'DASHBOARD_DUE_INQUIRIES',
  DASHBOARD_UPCOMING_FOLLOWUPS: 'DASHBOARD_UPCOMING_FOLLOWUPS',
  // Calendar
  CALENDAR_VIEW: 'CALENDAR_VIEW',
  CALENDAR_VIEW_ALL_JOBS: 'CALENDAR_VIEW_ALL_JOBS',
  CALENDAR_ADD_LEAD: 'CALENDAR_ADD_LEAD',
  CALENDAR_ADD_JOB: 'CALENDAR_ADD_JOB',
  CALENDAR_ADD_QUOTATION: 'CALENDAR_ADD_QUOTATION',
  CALENDAR_ADD_TASKS: 'CALENDAR_ADD_TASKS',
  // Clients
  CLIENT_VIEW_LIST: 'CLIENT_VIEW_LIST',
  CLIENT_VIEW_SINGLE: 'CLIENT_VIEW_SINGLE',
  CLIENT_ADD: 'CLIENT_ADD',
  CLIENT_EDIT_FULL: 'CLIENT_EDIT_FULL',
  CLIENT_CHANGE_STATUS: 'CLIENT_CHANGE_STATUS',
  CLIENT_ARCHIVE: 'CLIENT_ARCHIVE',
  // Leads
  LEAD_VIEW_LIST: 'LEAD_VIEW_LIST',
  LEAD_VIEW_SINGLE: 'LEAD_VIEW_SINGLE',
  LEAD_ADD: 'LEAD_ADD',
  LEAD_EDIT_FULL: 'LEAD_EDIT_FULL',
  LEAD_CHANGE_STATUS: 'LEAD_CHANGE_STATUS',
  LEAD_CHANGE_PRIORITY: 'LEAD_CHANGE_PRIORITY',
  LEAD_ARCHIVE: 'LEAD_ARCHIVE',
  // Jobs
  JOB_VIEW_LIST: 'JOB_VIEW_LIST',
  JOB_VIEW_SINGLE: 'JOB_VIEW_SINGLE',
  JOB_ADD: 'JOB_ADD',
  JOB_EDIT_FULL: 'JOB_EDIT_FULL',
  JOB_CHANGE_STATUS: 'JOB_CHANGE_STATUS',
  JOB_CHANGE_PRIORITY: 'JOB_CHANGE_PRIORITY',
  JOB_ARCHIVE: 'JOB_ARCHIVE',
  // Quotations
  QUOTATION_VIEW_LIST: 'QUOTATION_VIEW_LIST',
  QUOTATION_VIEW_SINGLE: 'QUOTATION_VIEW_SINGLE',
  QUOTATION_ADD: 'QUOTATION_ADD',
  QUOTATION_EDIT_FULL: 'QUOTATION_EDIT_FULL',
  QUOTATION_CHANGE_STATUS: 'QUOTATION_CHANGE_STATUS',
  QUOTATION_CHANGE_PRIORITY: 'QUOTATION_CHANGE_PRIORITY',
  QUOTATION_ARCHIVE: 'QUOTATION_ARCHIVE',
  // Invoice
  INVOICE_VIEW_LIST: 'INVOICE_VIEW_LIST',
  INVOICE_VIEW_SINGLE: 'INVOICE_VIEW_SINGLE',
  INVOICE_ADD: 'INVOICE_ADD',
  INVOICE_EDIT_FULL: 'INVOICE_EDIT_FULL',
  INVOICE_CHANGE_STATUS: 'INVOICE_CHANGE_STATUS',
  INVOICE_ARCHIVE: 'INVOICE_ARCHIVE',
  // Tasks
  TASK_VIEW_LIST: 'TASK_VIEW_LIST',
  TASK_VIEW_SINGLE: 'TASK_VIEW_SINGLE',
  TASK_ADD: 'TASK_ADD',
  TASK_EDIT_FULL: 'TASK_EDIT_FULL',
  TASK_CHANGE_STATUS: 'TASK_CHANGE_STATUS',
  TASK_ARCHIVE: 'TASK_ARCHIVE',
  // Inventory
  INVENTORY_VIEW_LIST: 'INVENTORY_VIEW_LIST',
  INVENTORY_VIEW_SINGLE: 'INVENTORY_VIEW_SINGLE',
  INVENTORY_ADD: 'INVENTORY_ADD',
  INVENTORY_EDIT_FULL: 'INVENTORY_EDIT_FULL',
  INVENTORY_CHANGE_STATUS: 'INVENTORY_CHANGE_STATUS',
  INVENTORY_ARCHIVE: 'INVENTORY_ARCHIVE',
  // User Management
  USER_VIEW_LIST: 'USER_VIEW_LIST',
  USER_VIEW_SINGLE: 'USER_VIEW_SINGLE',
  USER_ADD: 'USER_ADD',
  USER_EDIT: 'USER_EDIT',
  USER_RESEND_CREDENTIALS: 'USER_RESEND_CREDENTIALS',
  USER_ARCHIVE: 'USER_ARCHIVE',
  // Time Clock
  TIMECLOCK_VIEW: 'TIMECLOCK_VIEW',
  TIMECLOCK_EDIT: 'TIMECLOCK_EDIT',
  // Timesheet
  TIMESHEET_VIEW: 'TIMESHEET_VIEW',
  TIMESHEET_EDIT: 'TIMESHEET_EDIT',
  TIMESHEET_APPROVE_HOLD_CANCEL: 'TIMESHEET_APPROVE_HOLD_CANCEL',
  TIMESHEET_MANAGER_NOTES: 'TIMESHEET_MANAGER_NOTES',
  TIMESHEET_VIEW_PHOTOS: 'TIMESHEET_VIEW_PHOTOS',
  TIMESHEET_DOWNLOAD_PHOTOS: 'TIMESHEET_DOWNLOAD_PHOTOS',
  TIMESHEET_EXPORT_PDF: 'TIMESHEET_EXPORT_PDF',
  // Permission management (admin only by default)
  PERMISSION_MANAGE: 'PERMISSION_MANAGE',
  // Holiday management
  HOLIDAY_MANAGE: 'HOLIDAY_MANAGE',
  // Pipeline / Opportunity Hub
  PIPELINE_VIEW: 'PIPELINE_VIEW',
  PIPELINE_CONVERT_LEAD: 'PIPELINE_CONVERT_LEAD',
  PIPELINE_CONVERT_QUOTE: 'PIPELINE_CONVERT_QUOTE',
  PIPELINE_CONVERT_JOB: 'PIPELINE_CONVERT_JOB',
  PIPELINE_JOB_FORM: 'PIPELINE_JOB_FORM',
  PIPELINE_REPORT_VIEW: 'PIPELINE_REPORT_VIEW',
  // Soft-delete / restore
  SOFT_DELETE: 'SOFT_DELETE',
  SOFT_DELETE_RESTORE: 'SOFT_DELETE_RESTORE',
  // Lead/Quote extras
  LEAD_SOFT_DELETE: 'LEAD_SOFT_DELETE',
  QUOTATION_SOFT_DELETE: 'QUOTATION_SOFT_DELETE',
  JOB_SOFT_DELETE: 'JOB_SOFT_DELETE',
  INVOICE_SOFT_DELETE: 'INVOICE_SOFT_DELETE',
  CLIENT_SOFT_DELETE: 'CLIENT_SOFT_DELETE',
  // Billing / invoicing (granular)
  BILLING_VIEW: 'BILLING_VIEW',
  BILLING_CONFIG_EDIT: 'EDIT_BILLING_CONFIG',
  INVOICE_GENERATE: 'GENERATE_INVOICE',
  // Shift approvals (distinct from timesheet approve)
  SHIFT_APPROVE: 'APPROVE_SHIFT',
  // Generic report/export
  REPORT_EXPORT: 'EXPORT_REPORT',
  /** View job/quotation pricing in UI and API */
  PRICING_VIEW: 'pricing.view',
} as const;

/** Canonical permission key type — use for directives, guards, and API checks. */
export type PermissionKey = (typeof PERMISSION)[keyof typeof PERMISSION];

/** @deprecated Prefer PermissionKey */
export type PermissionCode = PermissionKey;

/** Permission groups by main module for Permission Management UI. */
export interface PermissionGroup {
  id: string;
  label: string;
  permissions: readonly string[];
}

export const PERMISSION_GROUPS: PermissionGroup[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    permissions: [
      PERMISSION.DASHBOARD_VIEW_JOBS_BY_STATUS,
      PERMISSION.DASHBOARD_ADD_CLIENT,
      PERMISSION.DASHBOARD_ADD_LEAD,
      PERMISSION.DASHBOARD_ADD_JOB,
      PERMISSION.DASHBOARD_ADD_QUOTATION,
      PERMISSION.DASHBOARD_ADD_INVOICE,
      PERMISSION.DASHBOARD_ADD_STAFF,
      PERMISSION.DASHBOARD_SUMMARY_PENDING,
      PERMISSION.DASHBOARD_SUMMARY_JOBS,
      PERMISSION.DASHBOARD_JOBS_TABLE,
      PERMISSION.DASHBOARD_DUE_INQUIRIES,
      PERMISSION.DASHBOARD_UPCOMING_FOLLOWUPS,
    ],
  },
  {
    id: 'calendar',
    label: 'Calendar',
    permissions: [
      PERMISSION.CALENDAR_VIEW,
      PERMISSION.CALENDAR_VIEW_ALL_JOBS,
      PERMISSION.CALENDAR_ADD_LEAD,
      PERMISSION.CALENDAR_ADD_JOB,
      PERMISSION.CALENDAR_ADD_QUOTATION,
      PERMISSION.CALENDAR_ADD_TASKS,
    ],
  },
  {
    id: 'clients',
    label: 'Clients',
    permissions: [
      PERMISSION.CLIENT_VIEW_LIST,
      PERMISSION.CLIENT_VIEW_SINGLE,
      PERMISSION.CLIENT_ADD,
      PERMISSION.CLIENT_EDIT_FULL,
      PERMISSION.CLIENT_CHANGE_STATUS,
      PERMISSION.CLIENT_ARCHIVE,
    ],
  },
  {
    id: 'leads',
    label: 'Leads',
    permissions: [
      PERMISSION.LEAD_VIEW_LIST,
      PERMISSION.LEAD_VIEW_SINGLE,
      PERMISSION.LEAD_ADD,
      PERMISSION.LEAD_EDIT_FULL,
      PERMISSION.LEAD_CHANGE_STATUS,
      PERMISSION.LEAD_CHANGE_PRIORITY,
      PERMISSION.LEAD_ARCHIVE,
    ],
  },
  {
    id: 'jobs',
    label: 'Jobs',
    permissions: [
      PERMISSION.JOB_VIEW_LIST,
      PERMISSION.JOB_VIEW_SINGLE,
      PERMISSION.JOB_ADD,
      PERMISSION.JOB_EDIT_FULL,
      PERMISSION.JOB_CHANGE_STATUS,
      PERMISSION.JOB_CHANGE_PRIORITY,
      PERMISSION.JOB_ARCHIVE,
      PERMISSION.PRICING_VIEW,
    ],
  },
  {
    id: 'quotations',
    label: 'Quotations',
    permissions: [
      PERMISSION.QUOTATION_VIEW_LIST,
      PERMISSION.QUOTATION_VIEW_SINGLE,
      PERMISSION.QUOTATION_ADD,
      PERMISSION.QUOTATION_EDIT_FULL,
      PERMISSION.QUOTATION_CHANGE_STATUS,
      PERMISSION.QUOTATION_CHANGE_PRIORITY,
      PERMISSION.QUOTATION_ARCHIVE,
    ],
  },
  {
    id: 'invoice',
    label: 'Invoice',
    permissions: [
      PERMISSION.INVOICE_VIEW_LIST,
      PERMISSION.INVOICE_VIEW_SINGLE,
      PERMISSION.INVOICE_ADD,
      PERMISSION.INVOICE_EDIT_FULL,
      PERMISSION.INVOICE_CHANGE_STATUS,
      PERMISSION.INVOICE_ARCHIVE,
      PERMISSION.INVOICE_GENERATE,
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    permissions: [
      PERMISSION.BILLING_VIEW,
      PERMISSION.BILLING_CONFIG_EDIT,
    ],
  },
  {
    id: 'tasks',
    label: 'Tasks',
    permissions: [
      PERMISSION.TASK_VIEW_LIST,
      PERMISSION.TASK_VIEW_SINGLE,
      PERMISSION.TASK_ADD,
      PERMISSION.TASK_EDIT_FULL,
      PERMISSION.TASK_CHANGE_STATUS,
      PERMISSION.TASK_ARCHIVE,
    ],
  },
  {
    id: 'inventory',
    label: 'Inventory',
    permissions: [
      PERMISSION.INVENTORY_VIEW_LIST,
      PERMISSION.INVENTORY_VIEW_SINGLE,
      PERMISSION.INVENTORY_ADD,
      PERMISSION.INVENTORY_EDIT_FULL,
      PERMISSION.INVENTORY_CHANGE_STATUS,
      PERMISSION.INVENTORY_ARCHIVE,
    ],
  },
  {
    id: 'user-management',
    label: 'User Management',
    permissions: [
      PERMISSION.USER_VIEW_LIST,
      PERMISSION.USER_VIEW_SINGLE,
      PERMISSION.USER_ADD,
      PERMISSION.USER_EDIT,
      PERMISSION.USER_RESEND_CREDENTIALS,
      PERMISSION.USER_ARCHIVE,
    ],
  },
  {
    id: 'time-clock',
    label: 'Time Clock',
    permissions: [
      PERMISSION.TIMECLOCK_VIEW,
      PERMISSION.TIMECLOCK_EDIT,
    ],
  },
  {
    id: 'timesheet',
    label: 'Timesheet',
    permissions: [
      PERMISSION.TIMESHEET_VIEW,
      PERMISSION.TIMESHEET_EDIT,
      PERMISSION.TIMESHEET_APPROVE_HOLD_CANCEL,
      PERMISSION.TIMESHEET_MANAGER_NOTES,
      PERMISSION.TIMESHEET_VIEW_PHOTOS,
      PERMISSION.TIMESHEET_DOWNLOAD_PHOTOS,
      PERMISSION.TIMESHEET_EXPORT_PDF,
      PERMISSION.SHIFT_APPROVE,
      PERMISSION.REPORT_EXPORT,
    ],
  },
  {
    id: 'permission-management',
    label: 'Permission Management',
    permissions: [PERMISSION.PERMISSION_MANAGE],
  },
  {
    id: 'holiday-management',
    label: 'Holiday Management',
    permissions: [PERMISSION.HOLIDAY_MANAGE],
  },
  {
    id: 'pipeline',
    label: 'Sales Pipeline',
    permissions: [
      PERMISSION.PIPELINE_VIEW,
      PERMISSION.PIPELINE_CONVERT_LEAD,
      PERMISSION.PIPELINE_CONVERT_QUOTE,
      PERMISSION.PIPELINE_CONVERT_JOB,
      PERMISSION.PIPELINE_JOB_FORM,
      PERMISSION.PIPELINE_REPORT_VIEW,
    ],
  },
  {
    id: 'soft-delete',
    label: 'Soft-delete & Restore',
    permissions: [
      PERMISSION.SOFT_DELETE,
      PERMISSION.SOFT_DELETE_RESTORE,
      PERMISSION.LEAD_SOFT_DELETE,
      PERMISSION.QUOTATION_SOFT_DELETE,
      PERMISSION.JOB_SOFT_DELETE,
      PERMISSION.INVOICE_SOFT_DELETE,
      PERMISSION.CLIENT_SOFT_DELETE,
    ],
  },
];

export const ROLES = {
  ADMIN: 'ADMIN',
  MANAGEMENT: 'MANAGEMENT',
  SUPERVISOR: 'SUPERVISOR',
  STAFF: 'STAFF',
} as const;

/** Normalize role strings from API/storage (handles common typos e.g. SUPERVISER). */
export function normalizeRoleName(role: string): string {
  const r = (role || '').toUpperCase().trim();
  if (r === 'SUPERVISER' || r === 'SUPERVISOR') {
    return ROLES.SUPERVISOR;
  }
  if (r === 'ADMIN' || r === 'ADMINISTRATOR') {
    return ROLES.ADMIN;
  }
  if (r === 'MANAGEMENT' || r === 'MANAGER') {
    return ROLES.MANAGEMENT;
  }
  if (r === 'STAFF') {
    return ROLES.STAFF;
  }
  return r;
}

/** Role names that can be assigned to jobs/tasks in "Assign Staff" dropdowns (Admin, Supervisor, Staff). */
export const ASSIGNABLE_STAFF_ROLES: string[] = [
  ROLES.ADMIN,
  ROLES.MANAGEMENT,
  ROLES.SUPERVISOR,
  ROLES.STAFF,
];

/** Role names eligible for Sales Representative on quotations (Admin, Supervisor only). */
export const SALES_REP_ROLES: string[] = [ROLES.ADMIN, ROLES.SUPERVISOR];

/** Human-readable labels for permissions (for Permission Management UI). */
export const PERMISSION_LABELS: Record<string, string> = {
  [PERMISSION.DASHBOARD_VIEW_JOBS_BY_STATUS]: 'View all jobs by status',
  [PERMISSION.DASHBOARD_ADD_CLIENT]: 'Add Client',
  [PERMISSION.DASHBOARD_ADD_LEAD]: 'Add Lead',
  [PERMISSION.DASHBOARD_ADD_JOB]: 'Add Job',
  [PERMISSION.DASHBOARD_ADD_QUOTATION]: 'Add Quotation',
  [PERMISSION.DASHBOARD_ADD_INVOICE]: 'Add Invoice',
  [PERMISSION.DASHBOARD_ADD_STAFF]: 'Add Staff',
  [PERMISSION.DASHBOARD_SUMMARY_PENDING]: 'Summary Pending Quotes/Invoices/Jobs',
  [PERMISSION.DASHBOARD_SUMMARY_JOBS]: 'Summary All Jobs and Completed Jobs',
  [PERMISSION.DASHBOARD_JOBS_TABLE]: 'Jobs Table',
  [PERMISSION.DASHBOARD_DUE_INQUIRIES]: 'Due Inquiries',
  [PERMISSION.DASHBOARD_UPCOMING_FOLLOWUPS]: 'Upcoming Followups',
  [PERMISSION.CALENDAR_VIEW]: 'View Calendar',
  [PERMISSION.CALENDAR_VIEW_ALL_JOBS]: 'View all jobs',
  [PERMISSION.CALENDAR_ADD_LEAD]: 'Add Lead',
  [PERMISSION.CALENDAR_ADD_JOB]: 'Add Job',
  [PERMISSION.CALENDAR_ADD_QUOTATION]: 'Add Quotation',
  [PERMISSION.CALENDAR_ADD_TASKS]: 'Add Tasks',
  [PERMISSION.CLIENT_VIEW_LIST]: 'View Clients list',
  [PERMISSION.CLIENT_VIEW_SINGLE]: 'View client - single',
  [PERMISSION.CLIENT_ADD]: 'Add Client',
  [PERMISSION.CLIENT_EDIT_FULL]: 'Edit Client - full access',
  [PERMISSION.CLIENT_CHANGE_STATUS]: 'Change Status',
  [PERMISSION.CLIENT_ARCHIVE]: 'Archive',
  [PERMISSION.LEAD_VIEW_LIST]: 'View Leads list',
  [PERMISSION.LEAD_VIEW_SINGLE]: 'View Lead - single',
  [PERMISSION.LEAD_ADD]: 'Add Lead',
  [PERMISSION.LEAD_EDIT_FULL]: 'Edit Lead - full access',
  [PERMISSION.LEAD_CHANGE_STATUS]: 'Change Status',
  [PERMISSION.LEAD_CHANGE_PRIORITY]: 'Change Priority',
  [PERMISSION.LEAD_ARCHIVE]: 'Archive',
  [PERMISSION.JOB_VIEW_LIST]: 'View all jobs list',
  [PERMISSION.JOB_VIEW_SINGLE]: 'View Job – Single',
  [PERMISSION.JOB_ADD]: 'Add Job',
  [PERMISSION.JOB_EDIT_FULL]: 'Edit Job - full access',
  [PERMISSION.JOB_CHANGE_STATUS]: 'Change Status',
  [PERMISSION.JOB_CHANGE_PRIORITY]: 'Change Priority',
  [PERMISSION.JOB_ARCHIVE]: 'Archive',
  [PERMISSION.QUOTATION_VIEW_LIST]: 'View Quotations list',
  [PERMISSION.QUOTATION_VIEW_SINGLE]: 'View Quotation - single',
  [PERMISSION.QUOTATION_ADD]: 'Add Quotation',
  [PERMISSION.QUOTATION_EDIT_FULL]: 'Edit Quotation - full access',
  [PERMISSION.QUOTATION_CHANGE_STATUS]: 'Change Status',
  [PERMISSION.QUOTATION_CHANGE_PRIORITY]: 'Change Priority',
  [PERMISSION.QUOTATION_ARCHIVE]: 'Archive',
  [PERMISSION.INVOICE_VIEW_LIST]: 'View Invoices list',
  [PERMISSION.INVOICE_VIEW_SINGLE]: 'View Invoice - single',
  [PERMISSION.INVOICE_ADD]: 'Add Invoice',
  [PERMISSION.INVOICE_EDIT_FULL]: 'Edit Invoice - full access',
  [PERMISSION.INVOICE_CHANGE_STATUS]: 'Change Status',
  [PERMISSION.INVOICE_ARCHIVE]: 'Archive',
  [PERMISSION.TASK_VIEW_LIST]: 'View Tasks list',
  [PERMISSION.TASK_VIEW_SINGLE]: 'View Task - single',
  [PERMISSION.TASK_ADD]: 'Add Tasks',
  [PERMISSION.TASK_EDIT_FULL]: 'Edit task - full access',
  [PERMISSION.TASK_CHANGE_STATUS]: 'Change Status',
  [PERMISSION.TASK_ARCHIVE]: 'Archive',
  [PERMISSION.INVENTORY_VIEW_LIST]: 'View Inventory list',
  [PERMISSION.INVENTORY_VIEW_SINGLE]: 'View Inventory - single',
  [PERMISSION.INVENTORY_ADD]: 'Add Inventory',
  [PERMISSION.INVENTORY_EDIT_FULL]: 'Edit inventory - full access',
  [PERMISSION.INVENTORY_CHANGE_STATUS]: 'Change Status',
  [PERMISSION.INVENTORY_ARCHIVE]: 'Archive',
  [PERMISSION.USER_VIEW_LIST]: 'View Users List',
  [PERMISSION.USER_VIEW_SINGLE]: 'View User – Single',
  [PERMISSION.USER_ADD]: 'Add User',
  [PERMISSION.USER_EDIT]: 'Edit User',
  [PERMISSION.USER_RESEND_CREDENTIALS]: 'Resend credentials',
  [PERMISSION.USER_ARCHIVE]: 'Archive',
  [PERMISSION.TIMECLOCK_VIEW]: 'View Time Clock Table',
  [PERMISSION.TIMECLOCK_EDIT]: 'Edit Time Clock Table',
  [PERMISSION.TIMESHEET_VIEW]: 'View User Timesheet',
  [PERMISSION.TIMESHEET_EDIT]: 'Edit Timesheet',
  [PERMISSION.TIMESHEET_APPROVE_HOLD_CANCEL]: 'Approve/Hold/Cancel',
  [PERMISSION.TIMESHEET_MANAGER_NOTES]: 'Add Manager Notes',
  [PERMISSION.TIMESHEET_VIEW_PHOTOS]: 'View Before and After Photos',
  [PERMISSION.TIMESHEET_DOWNLOAD_PHOTOS]: 'Download Photos',
  [PERMISSION.TIMESHEET_EXPORT_PDF]: 'Export as PDF',
  [PERMISSION.PERMISSION_MANAGE]: 'Permission Management',
  [PERMISSION.HOLIDAY_MANAGE]: 'Holiday Management',
  [PERMISSION.PIPELINE_VIEW]: 'View Sales Pipeline',
  [PERMISSION.PIPELINE_CONVERT_LEAD]: 'Convert Lead to Quotation',
  [PERMISSION.PIPELINE_CONVERT_QUOTE]: 'Convert Quotation to Job',
  [PERMISSION.PIPELINE_CONVERT_JOB]: 'Convert Job to Invoice',
  [PERMISSION.PIPELINE_JOB_FORM]: 'Use Pipeline Job Form',
  [PERMISSION.PIPELINE_REPORT_VIEW]: 'View Monthly Sales Reports',
  [PERMISSION.SOFT_DELETE]: 'Soft-delete records',
  [PERMISSION.SOFT_DELETE_RESTORE]: 'Restore soft-deleted records',
  [PERMISSION.LEAD_SOFT_DELETE]: 'Soft-delete Leads',
  [PERMISSION.QUOTATION_SOFT_DELETE]: 'Soft-delete Quotations',
  [PERMISSION.JOB_SOFT_DELETE]: 'Soft-delete Jobs',
  [PERMISSION.INVOICE_SOFT_DELETE]: 'Soft-delete Invoices',
  [PERMISSION.CLIENT_SOFT_DELETE]: 'Soft-delete Clients',
  [PERMISSION.BILLING_VIEW]: 'View Billing',
  [PERMISSION.BILLING_CONFIG_EDIT]: 'Edit Billing Config',
  [PERMISSION.INVOICE_GENERATE]: 'Generate Invoice',
  [PERMISSION.SHIFT_APPROVE]: 'Approve Shift',
  [PERMISSION.REPORT_EXPORT]: 'Export Reports',
};

/** Default: Admin has all, Supervisor per spec, Staff none. */
const DEFAULT_ADMIN_PERMISSIONS: string[] = Object.values(PERMISSION);
const DEFAULT_SUPERVISOR_PERMISSIONS: string[] = [
  PERMISSION.DASHBOARD_VIEW_JOBS_BY_STATUS,
  PERMISSION.DASHBOARD_ADD_STAFF,
  PERMISSION.CALENDAR_VIEW,
  PERMISSION.CALENDAR_VIEW_ALL_JOBS,
  PERMISSION.CALENDAR_ADD_JOB,
  PERMISSION.CALENDAR_ADD_TASKS,
  PERMISSION.JOB_VIEW_LIST,
  PERMISSION.JOB_VIEW_SINGLE,
  PERMISSION.JOB_ADD,
  PERMISSION.TASK_VIEW_LIST,
  PERMISSION.TASK_VIEW_SINGLE,
  PERMISSION.TASK_ADD,
  PERMISSION.TASK_EDIT_FULL,
  PERMISSION.TASK_CHANGE_STATUS,
  PERMISSION.TASK_ARCHIVE,
  PERMISSION.INVENTORY_VIEW_LIST,
  PERMISSION.INVENTORY_VIEW_SINGLE,
  PERMISSION.INVENTORY_ADD,
  PERMISSION.INVENTORY_EDIT_FULL,
  PERMISSION.INVENTORY_CHANGE_STATUS,
  PERMISSION.INVENTORY_ARCHIVE,
  PERMISSION.USER_VIEW_LIST,
  PERMISSION.USER_VIEW_SINGLE,
  PERMISSION.USER_ADD,
  PERMISSION.USER_RESEND_CREDENTIALS,
  PERMISSION.USER_ARCHIVE,
  PERMISSION.TIMECLOCK_VIEW,
  PERMISSION.TIMECLOCK_EDIT,
  PERMISSION.TIMESHEET_VIEW,
  PERMISSION.TIMESHEET_EDIT,
  PERMISSION.TIMESHEET_APPROVE_HOLD_CANCEL,
  PERMISSION.TIMESHEET_MANAGER_NOTES,
  PERMISSION.TIMESHEET_VIEW_PHOTOS,
  PERMISSION.TIMESHEET_DOWNLOAD_PHOTOS,
  PERMISSION.TIMESHEET_EXPORT_PDF,
  PERMISSION.HOLIDAY_MANAGE,
  PERMISSION.PIPELINE_VIEW,
  PERMISSION.PIPELINE_JOB_FORM,
  PERMISSION.SOFT_DELETE,
  PERMISSION.SHIFT_APPROVE,
  PERMISSION.REPORT_EXPORT,
  PERMISSION.BILLING_VIEW,
];
const DEFAULT_STAFF_PERMISSIONS: string[] = [];

const STORAGE_KEY_PERMISSIONS = 'acan_role_permissions_override';

/**
 * Supervisor must always keep these (job list/single view, calendar, mobile webview).
 * Permission Management cannot remove them from saved overrides.
 */
export const SUPERVISOR_LOCKED_PERMISSIONS: string[] = [
  PERMISSION.DASHBOARD_VIEW_JOBS_BY_STATUS,
  PERMISSION.CALENDAR_VIEW,
  PERMISSION.CALENDAR_VIEW_ALL_JOBS,
  PERMISSION.JOB_VIEW_LIST,
  PERMISSION.JOB_VIEW_SINGLE,
];

export function applySupervisorLockedPermissions(permissions: string[]): string[] {
  const set = new Set(permissions);
  SUPERVISOR_LOCKED_PERMISSIONS.forEach((code) => set.add(code));
  set.delete(PERMISSION.USER_EDIT);
  return Array.from(set);
}

/** Resolve effective permissions for a role (defaults, optional overrides, supervisor locks). */
export function resolveRolePermissions(
  role: string,
  overrides: Record<string, string[]> | null
): string[] {
  const normalizedRole = normalizeRoleName(role);
  const defaults = getDefaultRolePermissions();
  let list = defaults[normalizedRole] ?? defaults[ROLES.STAFF] ?? [];
  const overrideList =
    overrides?.[normalizedRole] ?? overrides?.[(role || '').toUpperCase().trim()];
  if (overrideList?.length) {
    list = [...overrideList];
  }
  if (normalizedRole === ROLES.SUPERVISOR) {
    list = applySupervisorLockedPermissions(list);
  }
  return list;
}

export function getDefaultRolePermissions(): Record<string, string[]> {
  // CR-73 — Management = Admin minus pay/bank edit (USER_EDIT covers pay & bank in this app).
  const management = DEFAULT_ADMIN_PERMISSIONS.filter(c => c !== PERMISSION.USER_EDIT);
  return {
    [ROLES.ADMIN]: [...DEFAULT_ADMIN_PERMISSIONS],
    [ROLES.MANAGEMENT]: management,
    [ROLES.SUPERVISOR]: [...DEFAULT_SUPERVISOR_PERMISSIONS],
    [ROLES.STAFF]: [...DEFAULT_STAFF_PERMISSIONS],
  };
}

export function getStoredPermissionOverrides(): Record<string, string[]> | null {
  try {
    const raw = getItem(STORAGE_KEY_PERMISSIONS);
    if (!raw) return null;
    return JSON.parse(raw) as Record<string, string[]>;
  } catch {
    return null;
  }
}

export function setStoredPermissionOverrides(overrides: Record<string, string[]>): void {
  setItem(STORAGE_KEY_PERMISSIONS, JSON.stringify(overrides));
}

export function getPermissionCodesList(): string[] {
  return Object.values(PERMISSION);
}

/** Returns permissions grouped by module for the Permission Management UI. */
export function getPermissionGroups(): PermissionGroup[] {
  return PERMISSION_GROUPS;
}
